// src/components/DashboardOverview.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Legend } from 'recharts';
import { formatDate } from '../utils';
import EisenhowerMatrixView from './EisenhowerMatrixView';
import AccountHealth from './AccountHealth';
import GoalCard from './GoalCard';
import IssueCard from './IssueCard';
import FrameworkAccordion from './FrameworkAccordion';
import ComponentDetailsModal from './ComponentDetailsModal';
import { ROUTES } from '../constants/routes';
import { DashboardSkeleton } from './LoadingSkeleton';
import { ALL_FRAMEWORK_COMPONENTS } from './frameworkDefinition';
import { StepperFilter, usePersistedFilters, SingleSelectDropdown, ToggleButton } from './FilterControls';
import SmartFilterBar from './SmartFilterBar';
import { ClientNarrativeSummary } from './NarrativeSummary';
import { GOAL_STATUS_OPTIONS, INITIATIVE_STATUS_OPTIONS, ISSUE_SEVERITY_OPTIONS, ISSUE_STATUS_OPTIONS, ACTION_ITEM_STATUS_OPTIONS } from '../constants';
import { useEntityNavigationContext } from './EntityNavigation';
import { useStaffContext } from '../contexts/StaffContext';

// Feature flag: Set to false to completely disable Eisenhower Matrix feature
const ENABLE_EISENHOWER_MATRIX = true;

// LocalStorage key for Eisenhower Matrix visibility
const EISENHOWER_VISIBLE_KEY = 'cortex-eisenhower-visible';

const COLORS = ['#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd', '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf'];

const DashboardOverview = () => {
    const { apiService, clientId, dateRange, setDateRange } = useOutletContext();
    const { getDriDisplayName } = useStaffContext();
    
    // Get filter expansion state from EntityNavigation context
    const { filtersExpanded } = useEntityNavigationContext();
    
    // Filters are always collapsible (controlled by EntityNavigation header)
    const filterVariant = 'collapsible';
    
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [loadingCommFrequency, setLoadingCommFrequency] = useState(false);
    const [dataLoaded, setDataLoaded] = useState(false); // Track if initial data is loaded
    // Use ref for cache to avoid re-render loops
    const commFrequencyCacheRef = useRef({
        day: { data: [], types: [], loaded: false },
        week: { data: [], types: [], loaded: false }
    });
    const [priorityCounts, setPriorityCounts] = useState({ 
        criticalIssues: 0, blockedInitiatives: 0, overdueActions: 0, neglectedGoals: 0,
        totalGoals: 0, totalIssues: 0, totalInitiatives: 0,
        allOpenGoals: 0, allOpenActionItems: 0, allOpenIssues: 0, allOpenInitiatives: 0,
    });
    const [goalsAtRisk, setGoalsAtRisk] = useState([]);
    const [criticalIssues, setCriticalIssues] = useState([]);
    const [sentimentData, setSentimentData] = useState([]);
    const [commFrequencyData, setCommFrequencyData] = useState([]);
    const [commTypes, setCommTypes] = useState([]);
    const [clientNarrative, setClientNarrative] = useState({
        narrativeAccountSummary: null,
        narrativeKeyDevelopments: null,
        narrativeRiskAreas: null,
        narrativeRecommendedActions: null,
        narrativeUpdatedAt: null,
    });
    
    const [granularity, setGranularity] = useState('week');
    const [chartDisplayMode, setChartDisplayMode] = useState('combined');
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    
    const [dris, setDris] = useState([]);
    const [selectedDris, setSelectedDris] = usePersistedFilters('selectedDris', []);
    
    // Eisenhower Matrix filters - persisted
    const [minImportanceFilter, setMinImportanceFilter] = usePersistedFilters('minImportance', 0);
    const [minUrgencyFilter, setMinUrgencyFilter] = usePersistedFilters('minUrgency', 0);
    
    // Entity-specific filters for the matrix (persisted)
    const [goalStatusFilter, setGoalStatusFilter] = usePersistedFilters('goalStatusFilter', '');
    const [initiativeStatusFilter, setInitiativeStatusFilter] = usePersistedFilters('initiativeStatusFilter', '');
    const [showBlockedInitiatives, setShowBlockedInitiatives] = usePersistedFilters('showBlockedInitiatives', false);
    const [issueSeverityFilter, setIssueSeverityFilter] = usePersistedFilters('issueSeverityFilter', '');
    const [issueStatusFilter, setIssueStatusFilter] = usePersistedFilters('issueStatusFilter', '');
    const [deadlineFilter, setDeadlineFilter] = usePersistedFilters('deadlineBeforeFilter', '');
    const [actionInitiativeFilter, setActionInitiativeFilter] = usePersistedFilters('actionInitiativeFilter', '');
    const [assigneeFilter, setAssigneeFilter] = usePersistedFilters('assigneeFilter', '');
    const [actionItemStatusFilter, setActionItemStatusFilter] = usePersistedFilters('actionItemStatusFilter', '');
    
    // Initiative list for action item filter
    const [initiativeListForFilter, setInitiativeListForFilter] = useState([]);
    // Assignee list for action item filter (uses DRIs list)
    
    // Eisenhower Matrix visibility - persisted to localStorage
    const [showEisenhowerMatrix, setShowEisenhowerMatrix] = useState(() => {
        const saved = localStorage.getItem(EISENHOWER_VISIBLE_KEY);
        return saved === 'true';
    });
    
    const toggleEisenhowerMatrix = () => {
        setShowEisenhowerMatrix(prev => {
            const newState = !prev;
            localStorage.setItem(EISENHOWER_VISIBLE_KEY, String(newState));
            return newState;
        });
    };
    
    const hasActiveFilters = selectedDris.length > 0 || minImportanceFilter > 0 || minUrgencyFilter > 0 || 
        goalStatusFilter !== '' || initiativeStatusFilter !== '' || showBlockedInitiatives || 
        issueSeverityFilter !== '' || issueStatusFilter !== '' || deadlineFilter !== '' || 
        actionInitiativeFilter !== '' || assigneeFilter !== '' || actionItemStatusFilter !== '';
    
    const handleClearAllFilters = () => {
        setSelectedDris([]);
        setMinImportanceFilter(0);
        setMinUrgencyFilter(0);
        setGoalStatusFilter('');
        setInitiativeStatusFilter('');
        setShowBlockedInitiatives(false);
        setIssueSeverityFilter('');
        setIssueStatusFilter('');
        setDeadlineFilter('');
        setActionInitiativeFilter('');
        setAssigneeFilter('');
        setActionItemStatusFilter('');
    };

    // Health score and framework state
    const [healthData, setHealthData] = useState(null);
    const [healthTrend, setHealthTrend] = useState([]);
    const [frameworkComponents, setFrameworkComponents] = useState([]);
    const [evaluationMetadata, setEvaluationMetadata] = useState(null); // Store evaluation metadata
    const [selectedComponent, setSelectedComponent] = useState(null);
    const [componentDetailsLoading, setComponentDetailsLoading] = useState(false);

    const loadDrisAndInitiatives = useCallback(async () => {
        if (!apiService || !clientId) return;
        try {
            const [driResult, initiativeResult] = await Promise.all([
                apiService.getClientDistinctDRIs(clientId),
                apiService.getClientDistinctInitiatives(clientId)
            ]);
            // Backend now returns normalized DRIs (staff names instead of emails)
            setDris(driResult.map(r => r.dri).filter(Boolean));
            setInitiativeListForFilter(initiativeResult.map(r => r.initiativeName).filter(Boolean));
        } catch (err) {
            console.error('Error loading DRIs/initiatives for client:', err);
        }
    }, [apiService, clientId]);

    const loadHealthData = useCallback(async () => {
        if (!apiService || !clientId) return;
        
        try {
            // Load health score and trend first (these don't depend on S3)
            const [healthScore, healthTrendData] = await Promise.all([
                apiService.getClientHealthScore(clientId),
                apiService.getClientHealthTrend(clientId, 90),
            ]);
            
            // Set health score and trend
            setHealthData(healthScore || null);
            setHealthTrend(healthTrendData || []);

            // Load evaluation details separately so S3 failures don't break health data
            let evaluationData = null;
            try {
                evaluationData = await apiService.getClientEvaluationDetails(clientId);
            } catch (evalErr) {
                console.warn('Evaluation details unavailable:', evalErr.message);
            }
            
            // Transform S3 evaluation data into component list for accordion
            if (evaluationData && evaluationData.observation_groups) {
                // Store evaluation metadata if available
                if (evaluationData._metadata) {
                    setEvaluationMetadata(evaluationData._metadata);
                }
                
                console.log('[DashboardOverview] Full evaluation data:', evaluationData);
                console.log('[DashboardOverview] First observation group:', evaluationData.observation_groups[0]);
                if (evaluationData.observation_groups[0]?.observations[0]) {
                    console.log('[DashboardOverview] First observation:', evaluationData.observation_groups[0].observations[0]);
                }
                
                // Create a lookup map for framework components to get partNumber and weight
                const frameworkMap = new Map();
                ALL_FRAMEWORK_COMPONENTS.forEach(comp => {
                    frameworkMap.set(comp.id, comp);
                });
                
                const components = evaluationData.observation_groups.flatMap(group =>
                    group.observations.map(obs => {
                        const frameworkComp = frameworkMap.get(obs.observation_name);
                        return {
                            componentId: obs.observation_name,
                            componentName: frameworkComp?.name || obs.observation_name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
                            isDetected: obs.is_detected,
                            present: obs.is_detected,  // Add 'present' field for FrameworkAccordion compatibility
                            partNumber: frameworkComp?.partNumber || 1,
                            weight: frameworkComp?.weight || 0,
                            groupName: group.group_name,
                            groupWeight: group.weight,
                            groupScore: group.score,
                            context: Array.isArray(obs.context) ? obs.context : (obs.context ? [obs.context] : []),
                            expectedValue: obs.expected_value,
                            // New fields for polarity support
                            positiveCount: obs.positive_count,
                            negativeCount: obs.negative_count,
                            netPolarity: obs.net_polarity,
                            polarity: obs.polarity,
                            // Pre-calculate meetsExpectation logic
                            meetsExpectation: (() => {
                                // New Logic: if net_polarity exists, > 0 is good (True), <= 0 is bad (False)
                                // User confirmed: "Positive Polarity" ALWAYS mean "Good for the Client Relationship"
                                if (typeof obs.net_polarity !== 'undefined') {
                                    return obs.net_polarity > 0;
                                }
                                // Fallback Old Logic
                                return obs.is_detected === obs.expected_value;
                            })()
                        };
                    })
                );
                
                console.log('[DashboardOverview] Transformed components (first 3):', components.slice(0, 3));
                setFrameworkComponents(components);
            } else {
                setFrameworkComponents([]);
            }
        } catch (err) {
            console.error('Error loading health data:', err);
            setFrameworkComponents([]);
        }
    }, [apiService, clientId]);

    const handleViewComponentDetails = async (component) => {
        if (!component) return;
        
        // Set loading state with the component ID being loaded
        setComponentDetailsLoading(component.componentId);
        
        // Fetch evaluation details WITH the observation name filter to get relevant communications
        try {
            const evaluationData = await apiService.getClientEvaluationDetails(
                clientId, 
                null, // evaluationId - use latest
                component.componentId // observationName - filter communications
            );
            
            // Set the component with the fetched evaluation metadata
            setSelectedComponent({
                ...component,
                _evaluationMetadata: evaluationData._metadata
            });
        } catch (err) {
            console.error('Error fetching component-specific evaluation details:', err);
            // Fall back to showing component without filtered metadata
            setSelectedComponent(component);
        } finally {
            setComponentDetailsLoading(false);
        }
    };

    const handleCloseComponentDetails = () => {
        setSelectedComponent(null);
    };

    const loadDashboardData = useCallback(async () => {
        if (!apiService || !clientId) return;
        
        // Build the endpoint to check cache
        const driFilter = selectedDris.length > 0 ? selectedDris : null;
        const startDate = dateRange?.start;
        const endDate = dateRange?.end;
        
        const params = new URLSearchParams();
        if (driFilter && driFilter.length > 0) {
            driFilter.forEach(dri => params.append('driFilter', dri));
        }
        if (startDate) params.append('startDate', startDate);
        if (endDate) params.append('endDate', endDate);
        const cacheEndpoint = `/clients/${clientId}/overview?${params.toString()}`;
        
        // Only show loading state if data is not cached
        const isCached = apiService.hasCache && apiService.hasCache(cacheEndpoint);
        if (!isCached) {
            setLoading(true);
        }
        setError(null);

        try {
            // Use the getClientOverview endpoint to fetch all data in a single request
            // Pass the date range to ensure counts are calculated for the selected period
            const overviewData = await apiService.getClientOverview(clientId, driFilter, startDate, endDate);

            // Extract and set all the data from the single response
            setPriorityCounts({
                criticalIssues: overviewData.priorityCounts?.criticalIssues ?? 0,
                blockedInitiatives: overviewData.priorityCounts?.blockedInitiatives ?? 0,
                overdueActions: overviewData.priorityCounts?.overdueActions ?? 0,
                neglectedGoals: overviewData.priorityCounts?.neglectedGoals ?? 0,
                totalGoals: overviewData.priorityCounts?.totalGoals ?? 0,
                totalIssues: overviewData.priorityCounts?.totalIssues ?? 0,
                totalInitiatives: overviewData.priorityCounts?.totalInitiatives ?? 0,
                allOpenGoals: overviewData.priorityCounts?.allOpenGoals ?? 0,
                allOpenActionItems: overviewData.priorityCounts?.allOpenActionItems ?? 0,
                allOpenIssues: overviewData.priorityCounts?.allOpenIssues ?? 0,
                allOpenInitiatives: overviewData.priorityCounts?.allOpenInitiatives ?? 0,
            });
            
            setGoalsAtRisk(overviewData.goalsAtRisk ?? []);
            setCriticalIssues(overviewData.criticalIssues ?? []);

            setClientNarrative({
                narrativeAccountSummary: overviewData.narrativeAccountSummary ?? null,
                narrativeKeyDevelopments: overviewData.narrativeKeyDevelopments ?? null,
                narrativeRiskAreas: overviewData.narrativeRiskAreas ?? null,
                narrativeRecommendedActions: overviewData.narrativeRecommendedActions ?? null,
                narrativeUpdatedAt: overviewData.narrativeUpdatedAt ?? null,
            });

            // Process sentiment data
            const rawSentimentResults = overviewData.sentimentTrend;
            const processedSentimentData = Array.isArray(rawSentimentResults)
                ? rawSentimentResults.map(item => ({
                    day: formatDate(item.weekBeginning),
                    avgSentiment: typeof item.avgSentiment === 'number' ? item.avgSentiment : parseFloat(item.avgSentiment)
                })).filter(item => item.day && typeof item.avgSentiment === 'number' && !isNaN(item.avgSentiment))
                : [];
            setSentimentData(processedSentimentData);

            // Process communication frequency data
            const rawCommFreq = overviewData.communicationFrequency;
            if (Array.isArray(rawCommFreq) && rawCommFreq.length > 0) {
                const uniqueTypes = [...new Set(rawCommFreq.map(item => item.commType))];
                const pivotedData = rawCommFreq.reduce((acc, item) => {
                    const period = formatDate(item.periodBeginning);
                    if (!period) return acc;
                    if (!acc[period]) {
                        acc[period] = { periodBeginning: period };
                        uniqueTypes.forEach(type => { acc[period][type] = 0; });
                    }
                    acc[period][item.commType] = item.periodCount;
                    return acc;
                }, {});
                const chartData = Object.values(pivotedData).sort((a, b) => new Date(a.periodBeginning) - new Date(b.periodBeginning));
                
                // Cache the results for 'week' granularity only (overview endpoint returns weekly data by default)
                commFrequencyCacheRef.current['week'] = { data: chartData, types: uniqueTypes, loaded: true };
                // Only update displayed data if we're currently on weekly view
                if (granularity === 'week') {
                    setCommFrequencyData(chartData);
                    setCommTypes(uniqueTypes);
                }
            }

        } catch (err) {
            console.error('Error loading dashboard overview data:', err);
            setError(`Overview Error: ${err.message}`);
        } finally {
            setLoading(false);
            setDataLoaded(true); // Mark that initial data has been loaded
            if (isInitialLoad) {
                setIsInitialLoad(false);
            }
        }
    }, [apiService, clientId, selectedDris, dateRange, isInitialLoad, granularity]);

    const loadCommFrequency = useCallback(async (targetGranularity = null) => {
        if (!apiService || !clientId) return;
        
        const gran = targetGranularity || granularity;
        
        // Check if we already have cached data for this granularity
        if (commFrequencyCacheRef.current[gran].loaded) {
            // Always update the displayed data when we have it
            setCommFrequencyData(commFrequencyCacheRef.current[gran].data);
            setCommTypes(commFrequencyCacheRef.current[gran].types);
            return;
        }
        
        setLoadingCommFrequency(true);

        try {
            const driFilter = selectedDris.length > 0 ? selectedDris : null;
            const startDate = dateRange?.start;
            const endDate = dateRange?.end;

            const commFrequencyResult = await apiService.getCommunicationFrequencyByType(clientId, driFilter, startDate, endDate, gran);

            const rawCommFreq = commFrequencyResult;
            if (Array.isArray(rawCommFreq)) {
                const uniqueTypes = [...new Set(rawCommFreq.map(item => item.commType))];
                const pivotedData = rawCommFreq.reduce((acc, item) => {
                    const period = formatDate(item.periodBeginning);
                    if (!period) return acc;
                    if (!acc[period]) {
                        acc[period] = { periodBeginning: period };
                        uniqueTypes.forEach(type => { acc[period][type] = 0; });
                    }
                    acc[period][item.commType] = item.periodCount;
                    return acc;
                }, {});
                const chartData = Object.values(pivotedData).sort((a, b) => new Date(a.periodBeginning) - new Date(b.periodBeginning));
                
                // Cache the results using ref
                commFrequencyCacheRef.current[gran] = { data: chartData, types: uniqueTypes, loaded: true };
                
                // Always update the displayed data
                setCommFrequencyData(chartData);
                setCommTypes(uniqueTypes);
            } else {
                setCommFrequencyData([]);
                setCommTypes([]);
            }
        } catch (err) {
            console.error('Error loading communication frequency data:', err);
        } finally {
            setLoadingCommFrequency(false);
        }
    }, [apiService, clientId, selectedDris, dateRange, granularity]);

    useEffect(() => {
        if (clientId) loadDrisAndInitiatives();
    }, [clientId, loadDrisAndInitiatives]);

    useEffect(() => {
        if (clientId) loadHealthData();
    }, [clientId, loadHealthData]);

    useEffect(() => {
        // Always load dashboard data on mount or when critical dependencies change
        // The loadDashboardData function will check cache to avoid loading spinner
        loadDashboardData();
    }, [clientId, selectedDris, dateRange, loadDashboardData]);

    // Separate effect for granularity changes - use cached data if available
    useEffect(() => {
        if (dataLoaded && clientId && apiService) {
            // Just trigger the load - it will handle cache checks and state updates
            loadCommFrequency(granularity);
        }
    // Intentionally omitting selectedDris and dateRange from dependencies.
    // Changes to selectedDris and dateRange are handled by a separate effect below
    // (the cache reset effect), which resets the cache and triggers a reload. This prevents unnecessary reloads on every filter change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [granularity, dataLoaded, clientId, apiService, loadCommFrequency]);

    // Clear cache and reload when filters or date range changes
    useEffect(() => {
        if (dataLoaded) {
            commFrequencyCacheRef.current = {
                day: { data: [], types: [], loaded: false },
                week: { data: [], types: [], loaded: false }
            };
        }
    }, [selectedDris, dateRange, dataLoaded]);

    const handleDriChange = (dri) => {
        setSelectedDris(prev => prev.includes(dri) ? prev.filter(d => d !== dri) : [...prev, dri]);
    };

    const handleGoalRiskClick = (goal) => {
        if (goal?.goalId) navigate(`${ROUTES.ACCOUNTS}/${clientId}/goals/${goal.goalId}`);
        else navigate(`${ROUTES.ACCOUNTS}/${clientId}/goals`);
    };

    const handleCriticalIssueClick = (issue) => {
        if (issue?.issueId) navigate(`${ROUTES.ACCOUNTS}/${clientId}/issues/${issue.issueId}`);
        else navigate(`${ROUTES.ACCOUNTS}/${clientId}/issues`);
    };

    // Always render filter bar, show loading/error states below it
    return (
        <>
            {/* SmartFilterBar for entity navigation */}
            <SmartFilterBar
                    dateRange={dateRange}
                    onDateRangeChange={setDateRange}
                    dris={dris}
                    selectedDris={selectedDris}
                    onDriChange={handleDriChange}
                    driDisplayFormatter={getDriDisplayName}
                    filterGroups={[
                        { id: 'goals', label: 'Goals & Initiatives', filters: [{ key: 'goalStatus' }, { key: 'initiativeStatus' }, { key: 'blockedInitiatives' }] },
                        { id: 'issues', label: 'Issues', filters: [{ key: 'severity' }, { key: 'issueStatus' }] },
                        { id: 'actions', label: 'Action Items', filters: [{ key: 'deadline' }, { key: 'initiative' }, { key: 'assignee' }, { key: 'actionStatus' }] },
                        { id: 'priority', label: 'Priority Matrix', filters: [{ key: 'minImportance', min: 0 }, { key: 'minUrgency', min: 0 }] }
                    ]}
                    filters={{
                        goalStatus: goalStatusFilter, initiativeStatus: initiativeStatusFilter, blockedInitiatives: showBlockedInitiatives,
                        severity: issueSeverityFilter, issueStatus: issueStatusFilter, deadline: deadlineFilter,
                        initiative: actionInitiativeFilter, assignee: assigneeFilter, actionStatus: actionItemStatusFilter, minImportance: minImportanceFilter, minUrgency: minUrgencyFilter,
                    }}
                    filterConfigs={{
                        goalStatus: { label: 'Goal Status', render: () => <SingleSelectDropdown id="goalStatusFilter" label="Goal Status" value={goalStatusFilter} onChange={setGoalStatusFilter} options={GOAL_STATUS_OPTIONS} allLabel="All" /> },
                        initiativeStatus: { label: 'Initiative Status', render: () => <SingleSelectDropdown id="initiativeStatusFilter" label="Init. Status" value={initiativeStatusFilter} onChange={(val) => { setInitiativeStatusFilter(val); if (val) setShowBlockedInitiatives(false); }} options={INITIATIVE_STATUS_OPTIONS} allLabel="All" /> },
                        blockedInitiatives: { label: 'Blocked Initiatives', render: () => <ToggleButton label="Blocked Init. Only" isActive={showBlockedInitiatives} onClick={() => { setShowBlockedInitiatives(!showBlockedInitiatives); if (!showBlockedInitiatives) setInitiativeStatusFilter(''); }} /> },
                        severity: { label: 'Severity', render: () => <SingleSelectDropdown id="severityFilter" label="Severity" value={issueSeverityFilter} onChange={setIssueSeverityFilter} options={ISSUE_SEVERITY_OPTIONS} allLabel="All" /> },
                        issueStatus: { label: 'Issue Status', render: () => <SingleSelectDropdown id="issueStatusFilter" label="Issue Status" value={issueStatusFilter} onChange={setIssueStatusFilter} options={ISSUE_STATUS_OPTIONS} allLabel="All" /> },
                        deadline: { label: 'Deadline', render: () => <SingleSelectDropdown id="deadlineFilter" label="Deadline" value={deadlineFilter} onChange={setDeadlineFilter} options={['overdue']} allLabel="All" capitalize={true} /> },
                        initiative: { label: 'Initiative', render: () => <SingleSelectDropdown id="actionInitiativeFilter" label="Initiative" value={actionInitiativeFilter} onChange={setActionInitiativeFilter} options={initiativeListForFilter} allLabel="All" capitalize={false} /> },
                        assignee: { label: 'Assignee', render: () => <SingleSelectDropdown id="assigneeFilter" label="Assignee" value={assigneeFilter} onChange={setAssigneeFilter} options={dris} allLabel="All" capitalize={false} /> },
                        actionStatus: { label: 'Action Status', render: () => <SingleSelectDropdown id="actionItemStatusFilter" label="Action Status" value={actionItemStatusFilter} onChange={setActionItemStatusFilter} options={ACTION_ITEM_STATUS_OPTIONS} allLabel="All" capitalize={true} /> },
                        minImportance: { label: 'Min Importance', min: 0, render: () => <StepperFilter value={minImportanceFilter} onChange={setMinImportanceFilter} min={0} max={10} step={1} label="Min Importance" tooltip="Filter items in the Priority Matrix by minimum importance score" /> },
                        minUrgency: { label: 'Min Urgency', min: 0, render: () => <StepperFilter value={minUrgencyFilter} onChange={setMinUrgencyFilter} min={0} max={3} step={0.5} label="Min Urgency" tooltip="Filter items in the Priority Matrix by minimum urgency score" /> }
                    }}
                    activeFilterChips={[
                        ...(goalStatusFilter ? [{ label: 'Goal', value: goalStatusFilter.charAt(0).toUpperCase() + goalStatusFilter.slice(1), onRemove: () => setGoalStatusFilter('') }] : []),
                        ...(initiativeStatusFilter ? [{ label: 'Initiative', value: initiativeStatusFilter.charAt(0).toUpperCase() + initiativeStatusFilter.slice(1), onRemove: () => setInitiativeStatusFilter('') }] : []),
                        ...(showBlockedInitiatives ? [{ label: 'Blocked', value: 'Yes', onRemove: () => setShowBlockedInitiatives(false) }] : []),
                        ...(issueSeverityFilter ? [{ label: 'Severity', value: issueSeverityFilter.charAt(0).toUpperCase() + issueSeverityFilter.slice(1), onRemove: () => setIssueSeverityFilter('') }] : []),
                        ...(issueStatusFilter ? [{ label: 'Issue Status', value: issueStatusFilter.charAt(0).toUpperCase() + issueStatusFilter.slice(1), onRemove: () => setIssueStatusFilter('') }] : []),
                        ...(deadlineFilter ? [{ label: 'Deadline', value: deadlineFilter.charAt(0).toUpperCase() + deadlineFilter.slice(1), onRemove: () => setDeadlineFilter('') }] : []),
                        ...(actionInitiativeFilter ? [{ label: 'Initiative', value: actionInitiativeFilter, onRemove: () => setActionInitiativeFilter('') }] : []),
                        ...(assigneeFilter ? [{ label: 'Assignee', value: assigneeFilter, onRemove: () => setAssigneeFilter('') }] : []),
                        ...(actionItemStatusFilter ? [{ label: 'Action Status', value: actionItemStatusFilter.charAt(0).toUpperCase() + actionItemStatusFilter.slice(1), onRemove: () => setActionItemStatusFilter('') }] : []),
                        ...(minImportanceFilter > 0 ? [{ label: 'Importance', value: `≥ ${minImportanceFilter}`, onRemove: () => setMinImportanceFilter(0) }] : []),
                        ...(minUrgencyFilter > 0 ? [{ label: 'Urgency', value: `≥ ${minUrgencyFilter}`, onRemove: () => setMinUrgencyFilter(0) }] : []),
                    ]}
                    onClearAll={handleClearAllFilters}
                    hasActiveFilters={hasActiveFilters}
                    variant={filterVariant}
                    isExpanded={filtersExpanded}
                />

            {/* Loading and error states - shown below filters */}
            {loading && <DashboardSkeleton />}
            {error && !loading && (
                <div 
                    className="p-4 rounded-md"
                    style={{ 
                        backgroundColor: 'var(--pallium-alert-error-bg)', 
                        borderLeft: '4px solid var(--pallium-error-red)', 
                        color: 'var(--pallium-error-red)' 
                    }}
                >
                    Error loading overview: {error}
                </div>
            )}

            {/* Main content - only shown when not loading and no error */}
            {!loading && !error && (
            <>
            {/* Account Health: Health Circle + Trend + Quick Metrics */}
            <div data-testid="overall-alignment">
                <AccountHealth
                    healthData={healthData}
                    healthTrend={healthTrend}
                    priorityCounts={priorityCounts}
                    clientId={clientId}
                />
            </div>

            <ClientNarrativeSummary
                accountSummary={clientNarrative.narrativeAccountSummary}
                keyDevelopments={clientNarrative.narrativeKeyDevelopments}
                riskAreas={clientNarrative.narrativeRiskAreas}
                recommendedActions={clientNarrative.narrativeRecommendedActions}
                updatedAt={clientNarrative.narrativeUpdatedAt}
                defaultExpanded={true}
                title="Account Briefing"
            />
                    
            {/* Eisenhower Matrix Section - Collapsible */}
            {ENABLE_EISENHOWER_MATRIX && (
                <div 
                    className="mt-6 rounded-xl overflow-hidden"
                    style={{ backgroundColor: 'var(--pallium-white)', boxShadow: 'var(--pallium-shadow-md)' }}
                    data-testid="priority-matrix"
                >
                    <button
                        onClick={toggleEisenhowerMatrix}
                        className="w-full px-4 py-3 md:px-6 md:py-4 flex items-center justify-between text-left transition-colors duration-200"
                        style={{ borderBottom: '1px solid var(--pallium-stroke-light)' }}
                        onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'var(--pallium-purple-0-5)'}
                        onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                        <div className="flex items-center">
                            <svg 
                                className={`h-5 w-5 mr-3 transition-transform duration-200 ${showEisenhowerMatrix ? 'rotate-90' : ''}`}
                                style={{ color: 'var(--pallium-primary-purple)' }}
                                xmlns="http://www.w3.org/2000/svg" 
                                viewBox="0 0 20 20" 
                                fill="currentColor"
                            >
                                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                            </svg>
                            <div>
                                <h3 className="text-lg font-medium" style={{ color: 'var(--pallium-text-primary)' }}>Priority Matrix</h3>
                                <p className="text-xs mt-0.5" style={{ color: 'var(--pallium-text-secondary)' }}>Eisenhower prioritization based on urgency and importance</p>
                            </div>
                        </div>
                    </button>
                    
                    {showEisenhowerMatrix && (
                        <div className="p-4 md:p-6">
                            <EisenhowerMatrixView
                                clientId={clientId}
                                typeFilter={['Goal', 'Initiative', 'ActionItem', 'Issue']}
                                driFilter={selectedDris}
                                minImportanceFilter={minImportanceFilter}
                                minUrgencyFilter={minUrgencyFilter}
                                goalStatusFilter={goalStatusFilter}
                                initiativeStatusFilter={initiativeStatusFilter}
                                issueSeverityFilter={issueSeverityFilter}
                                issueStatusFilter={issueStatusFilter}
                                deadlineFilter={deadlineFilter}
                                actionInitiativeFilter={actionInitiativeFilter}
                                assigneeFilter={assigneeFilter}
                                showBlockedInitiatives={showBlockedInitiatives}
                                actionItemStatusFilter={actionItemStatusFilter}
                            />
                        </div>
                    )}
                </div>
            )}

            {/* 50/50 Split: Framework (left) + Goals & Issues (right) */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
                {/* Left: Framework Accordion */}
                <div data-testid="framework-breakdown">
                    <FrameworkAccordion
                        healthData={healthData}
                        frameworkComponents={frameworkComponents}
                        onViewDetails={handleViewComponentDetails}
                        loadingComponentId={componentDetailsLoading}
                    />
                </div>

                {/* Right: Goals & Issues */}
                <div className="space-y-6">
                    {/* Top Priority Goals - Card View */}
                    <div 
                        className="rounded-xl p-6"
                        style={{ backgroundColor: 'var(--pallium-white)', boxShadow: 'var(--pallium-shadow-md)' }}
                    >
                        <h3 className="text-lg font-medium mb-2" style={{ color: 'var(--pallium-text-primary)' }}>
                            Top Priority Goals
                        </h3>
                        <p className="text-xs mb-4" style={{ color: 'var(--pallium-text-secondary)' }}>
                            Active goals ordered by priority score
                        </p>
                        <div className="space-y-3 max-h-96 overflow-y-auto">
                            {goalsAtRisk.length > 0 ? (
                                goalsAtRisk.map((goal, index) => (
                                    <GoalCard
                                        key={goal.goalId || index}
                                        goal={goal}
                                        clientId={clientId}
                                        onClick={handleGoalRiskClick}
                                    />
                                ))
                            ) : (
                                <div className="text-center text-sm py-8" style={{ color: 'var(--pallium-icon-gray)' }}>
                                    No active goals match the current filter.
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Critical Issues by Risk - Card View */}
                    <div 
                        className="rounded-xl p-6"
                        style={{ backgroundColor: 'var(--pallium-white)', boxShadow: 'var(--pallium-shadow-md)' }}
                    >
                        <h3 className="text-lg font-medium mb-2" style={{ color: 'var(--pallium-text-primary)' }}>
                            Critical Issues by Risk
                        </h3>
                        <p className="text-xs mb-4" style={{ color: 'var(--pallium-text-secondary)' }}>
                            Open issues ordered by risk score
                        </p>
                        <div className="space-y-3 max-h-96 overflow-y-auto">
                            {criticalIssues.length > 0 ? (
                                criticalIssues.map((issue, index) => (
                                    <IssueCard
                                        key={issue.issueId || index}
                                        issue={issue}
                                        clientId={clientId}
                                        onClick={handleCriticalIssueClick}
                                    />
                                ))
                            ) : (
                                <div className="text-center text-sm py-8" style={{ color: 'var(--pallium-icon-gray)' }}>
                                    No critical issues match the current filter.
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Communication Frequency - Full Width */}
            <div 
                className="rounded-xl p-6 mt-6"
                style={{ backgroundColor: 'var(--pallium-white)', boxShadow: 'var(--pallium-shadow-md)' }}
                data-testid="communication-frequency"
            >
                 <div className="flex justify-between items-center mb-4">
                     <h3 className="text-lg font-medium" style={{ color: 'var(--pallium-text-primary)' }}>
                         Communication Frequency
                     </h3>
                     <div className="flex gap-4">
                         <div className="flex gap-2">
                             <button
                                 onClick={() => setGranularity('day')}
                                 className="px-3 py-1 text-sm rounded transition-colors duration-200"
                                 style={granularity === 'day' 
                                     ? { backgroundColor: 'var(--pallium-primary-purple)', color: 'var(--pallium-white)' } 
                                     : { backgroundColor: 'var(--pallium-bg-secondary)', color: 'var(--pallium-text-secondary)' }}
                                 onMouseOver={(e) => { if (granularity !== 'day') e.target.style.backgroundColor = 'var(--pallium-stroke-light)'; }}
                                 onMouseOut={(e) => { if (granularity !== 'day') e.target.style.backgroundColor = 'var(--pallium-bg-secondary)'; }}
                             >
                                 Daily
                             </button>
                             <button
                                 onClick={() => setGranularity('week')}
                                 className="px-3 py-1 text-sm rounded transition-colors duration-200"
                                 style={granularity === 'week' 
                                     ? { backgroundColor: 'var(--pallium-primary-purple)', color: 'var(--pallium-white)' } 
                                     : { backgroundColor: 'var(--pallium-bg-secondary)', color: 'var(--pallium-text-secondary)' }}
                                 onMouseOver={(e) => { if (granularity !== 'week') e.target.style.backgroundColor = 'var(--pallium-stroke-light)'; }}
                                 onMouseOut={(e) => { if (granularity !== 'week') e.target.style.backgroundColor = 'var(--pallium-bg-secondary)'; }}
                             >
                                 Weekly
                             </button>
                         </div>
                         <div className="flex gap-2">
                             <button
                                 onClick={() => setChartDisplayMode('combined')}
                                 className="px-3 py-1 text-sm rounded transition-colors duration-200"
                                 style={chartDisplayMode === 'combined' 
                                     ? { backgroundColor: 'var(--pallium-primary-purple)', color: 'var(--pallium-white)' } 
                                     : { backgroundColor: 'var(--pallium-bg-secondary)', color: 'var(--pallium-text-secondary)' }}
                                 onMouseOver={(e) => { if (chartDisplayMode !== 'combined') e.target.style.backgroundColor = 'var(--pallium-stroke-light)'; }}
                                 onMouseOut={(e) => { if (chartDisplayMode !== 'combined') e.target.style.backgroundColor = 'var(--pallium-bg-secondary)'; }}
                             >
                                 Combined
                             </button>
                             <button
                                 onClick={() => setChartDisplayMode('separate')}
                                 className="px-3 py-1 text-sm rounded transition-colors duration-200"
                                 style={chartDisplayMode === 'separate' 
                                     ? { backgroundColor: 'var(--pallium-primary-purple)', color: 'var(--pallium-white)' } 
                                     : { backgroundColor: 'var(--pallium-bg-secondary)', color: 'var(--pallium-text-secondary)' }}
                                 onMouseOver={(e) => { if (chartDisplayMode !== 'separate') e.target.style.backgroundColor = 'var(--pallium-stroke-light)'; }}
                                 onMouseOut={(e) => { if (chartDisplayMode !== 'separate') e.target.style.backgroundColor = 'var(--pallium-bg-secondary)'; }}
                             >
                                 Separate
                             </button>
                         </div>
                     </div>
                 </div>
                 {loadingCommFrequency ? (
                     <div className="text-center text-sm py-8" style={{ color: 'var(--pallium-icon-gray)' }}>
                         Loading communication data...
                     </div>
                 ) : commFrequencyData.length > 0 && commTypes.length > 0 ? (
                      chartDisplayMode === 'combined' ? (
                          <ResponsiveContainer width="100%" height={250}>
                             <LineChart data={commFrequencyData} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
                               <CartesianGrid strokeDasharray="3 3" stroke="var(--pallium-stroke-light)" />
                               <XAxis dataKey="periodBeginning" tick={{fontSize: 10, fill: 'var(--pallium-text-secondary)'}} />
                               <YAxis allowDecimals={false} tick={{fontSize: 10, fill: 'var(--pallium-text-secondary)'}} />
                               <RechartsTooltip wrapperClassName="text-xs" />
                               <Legend />
                               {commTypes.map((type, index) => (
                                   <Line
                                       key={type}
                                       type="monotone"
                                       dataKey={type}
                                       stroke={COLORS[index % COLORS.length]}
                                       strokeWidth={2}
                                       dot={{r: 3}}
                                       activeDot={{r: 5}}
                                       name={type}
                                       connectNulls={true}
                                       isAnimationActive={isInitialLoad}
                                       animationDuration={1000}
                                   />
                               ))}
                             </LineChart>
                           </ResponsiveContainer>
                      ) : (
                          <div className="space-y-6">
                              {commTypes.map((type, index) => {
                                  const typeData = commFrequencyData.map(item => ({
                                      periodBeginning: item.periodBeginning,
                                      count: item[type] || 0
                                  }));
                                  return (
                                      <div key={type}>
                                          <h4 className="text-md font-medium mb-2 capitalize" style={{ color: 'var(--pallium-text-primary)' }}>
                                              {type}
                                          </h4>
                                          <ResponsiveContainer width="100%" height={200}>
                                              <LineChart data={typeData} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
                                                  <CartesianGrid strokeDasharray="3 3" stroke="var(--pallium-stroke-light)" />
                                                  <XAxis dataKey="periodBeginning" tick={{fontSize: 10, fill: 'var(--pallium-text-secondary)'}} />
                                                  <YAxis allowDecimals={false} tick={{fontSize: 10, fill: 'var(--pallium-text-secondary)'}} />
                                                  <RechartsTooltip wrapperClassName="text-xs" />
                                                  <Line
                                                      type="monotone"
                                                      dataKey="count"
                                                      stroke={COLORS[index % COLORS.length]}
                                                      strokeWidth={2}
                                                      dot={{r: 3}}
                                                      activeDot={{r: 5}}
                                                      name={type}
                                                      connectNulls={true}
                                                      isAnimationActive={false}
                                                  />
                                              </LineChart>
                                          </ResponsiveContainer>
                                      </div>
                                  );
                              })}
                          </div>
                      )
                  ) : (
                      <div className="text-center text-sm py-4" style={{ color: 'var(--pallium-icon-gray)' }}>
                          No communication data available for this period.
                      </div>
                  )}
             </div>

            <div 
                className="rounded-xl p-6 mt-6"
                style={{ backgroundColor: 'var(--pallium-white)', boxShadow: 'var(--pallium-shadow-md)' }}
                data-testid="sentiment-trend"
            >
                <h3 className="text-lg font-medium mb-4" style={{ color: 'var(--pallium-text-primary)' }}>
                    Sentiment Trend (Weekly)
                </h3>
                 {Array.isArray(sentimentData) && sentimentData.length > 0 ? (
                     <ResponsiveContainer width="100%" height={250}>
                        <LineChart data={sentimentData} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="var(--pallium-stroke-light)" />
                          <XAxis dataKey="day" tick={{fontSize: 10, fill: 'var(--pallium-text-secondary)'}} />
                          <YAxis tick={{fontSize: 10, fill: 'var(--pallium-text-secondary)'}} />
                          <RechartsTooltip
                              wrapperClassName="text-xs"
                              formatter={(value) => [`${(value * 100).toFixed(1)}%`, 'Avg Sentiment']}
                              labelFormatter={(value) => `Week Starting: ${value}`}
                          />
                          <Line
                              type="monotone"
                              dataKey="avgSentiment"
                              stroke="var(--pallium-primary-purple)"
                              strokeWidth={2}
                              dot={{r: 3}}
                              activeDot={{r: 5}}
                              name="Avg Sentiment"
                              connectNulls={true}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                 ) : (
                     <div className="text-center text-sm py-4" style={{ color: 'var(--pallium-icon-gray)' }}>
                         {loading ? 'Loading sentiment data...' : 'No sentiment data available for this period.'}
                     </div>
                 )}
            </div>
            </>
            )}

            {/* Component Details Modal - outside conditional so it works during any state */}
            {selectedComponent && (
                <ComponentDetailsModal
                    component={selectedComponent}
                    evaluationMetadata={evaluationMetadata}
                    onClose={handleCloseComponentDetails}
                />
            )}
        </>
    );
};

export default DashboardOverview;